/**
 * Maps in-progress form state to the `ProviderRequestBody` the API expects,
 * zeroing out fields that don't apply to the selected type/acquisition_mode
 * combination. Kept out of ProviderFormDialog.tsx to keep it under the
 * 200-line component cap.
 */

import type { ProviderRequestBody } from '../../types';
import type { ProviderFormData } from './providerFormTypes';

export function buildProviderRequestBody(
  form: ProviderFormData,
  extraAuthParams: Record<string, unknown> | null,
): ProviderRequestBody {
  const isDirect = form.type === 'oidc' && form.acquisition_mode === 'direct';
  const isBrokered = form.type === 'oidc' && form.acquisition_mode === 'brokered';
  return {
    name: form.name,
    display_name: form.display_name,
    enabled: form.enabled,
    type: form.type,
    acquisition_mode: form.type === 'oidc' ? form.acquisition_mode : null,
    broker_mechanism: isBrokered ? form.broker_mechanism : null,
    broker_alias: isBrokered ? form.broker_alias : null,
    issuer_url: form.type === 'oidc' ? form.issuer_url : null,
    authorization_endpoint: isDirect ? form.authorization_endpoint : null,
    token_endpoint: form.type === 'oidc' ? form.token_endpoint : null,
    client_id: form.type === 'oidc' ? form.client_id : null,
    client_secret: form.type === 'oidc' ? form.client_secret || null : null,
    // A GitHub App ignores requested scopes entirely -- the field is
    // hidden in the UI for that case, so the submitted body must not
    // carry a stale value either. This is the authoritative gate: it
    // runs regardless of whether form state was cleared on selection.
    scopes: isDirect && form.github_app_kind !== 'app' ? form.scopes : null,
    extra_auth_params: isDirect ? extraAuthParams : null,
    secret_label: form.type === 'static' ? form.secret_label : null,
    help_text: form.type === 'static' ? form.help_text : null,
    github_app_kind: isDirect ? form.github_app_kind : null,
  };
}
